
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RefreshCw, Wallet, BanknoteIcon, PiggyBank } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AdminWallet as WalletType } from '@/types/giftCardTypes';

interface AdminWalletProps {
  adminId: string;
  onBalanceUpdate?: (balance: number) => void;
}

const AdminWallet: React.FC<AdminWalletProps> = ({ adminId, onBalanceUpdate }) => {
  const { toast } = useToast();
  const [wallet, setWallet] = useState<WalletType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  
  // Settings state
  const [autoRecharge, setAutoRecharge] = useState<boolean>(false);
  const [minimumBalance, setMinimumBalance] = useState<string>('50');
  const [rechargeAmount, setRechargeAmount] = useState<string>('100');
  const [maintainMinimumBalance, setMaintainMinimumBalance] = useState<boolean>(false);
  
  useEffect(() => {
    if (!adminId) return;
    fetchWalletData();
  }, [adminId]);

  const fetchWalletData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'getAdminWallet',
          adminId
        }
      });

      if (error) throw new Error(error.message);
      
      if (data.wallet) {
        setWallet(data.wallet);
        setAutoRecharge(data.wallet.autoRecharge);
        setMinimumBalance(data.wallet.minimumBalance.toString());
        setRechargeAmount(data.wallet.autoRechargeAmount.toString());
        setMaintainMinimumBalance(data.wallet.maintainMinimumBalance || false);
        
        if (onBalanceUpdate) {
          onBalanceUpdate(data.wallet.balance);
        }
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitializeWallet = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'initializeAdminWallet',
          adminId,
          initialBalance: 0,  // New wallets start with zero balance
        }
      });

      if (error) throw new Error(error.message);
      
      if (data?.wallet) {
        setWallet(data.wallet);
        setAutoRecharge(data.wallet.autoRecharge);
        setMinimumBalance(data.wallet.minimumBalance.toString());
        setRechargeAmount(data.wallet.autoRechargeAmount.toString());
        
        toast({
          title: "Wallet initialized",
          description: "Your reward wallet has been created successfully"
        });
        
        if (onBalanceUpdate) {
          onBalanceUpdate(data.wallet.balance);
        }
      }
    } catch (error) {
      console.error('Error initializing wallet:', error);
      toast({
        title: "Error",
        description: "Failed to initialize wallet",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveWalletSettings = async () => {
    setIsUpdating(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'updateWalletSettings',
          adminId,
          autoRecharge,
          minimumBalance: parseFloat(minimumBalance),
          autoRechargeAmount: parseFloat(rechargeAmount),
          maintainMinimumBalance
        }
      });

      if (error) throw new Error(error.message);
      
      if (data?.success) {
        setWallet(data.wallet);
        toast({
          title: "Settings Updated",
          description: "Wallet settings have been updated successfully"
        });
      }
    } catch (error) {
      console.error('Error updating wallet settings:', error);
      toast({
        title: "Error",
        description: "Failed to update wallet settings",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center">
            <Wallet className="mr-2 h-5 w-5" /> Reward Wallet
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <div className="flex flex-col items-center space-y-2">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading wallet information...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!wallet) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center">
            <Wallet className="mr-2 h-5 w-5" /> Reward Wallet
          </CardTitle>
          <CardDescription>
            Your reward wallet needs to be initialized before you can purchase gift cards
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8 space-y-4">
          <BanknoteIcon className="h-16 w-16 text-muted-foreground" />
          <p className="text-center">
            You don't have a reward wallet set up yet. Initialize your wallet to start purchasing gift cards.
          </p>
          <Button onClick={handleInitializeWallet} disabled={isLoading}>
            {isLoading ? "Processing..." : "Initialize Wallet"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center">
          <Wallet className="mr-2 h-5 w-5" /> Reward Wallet
        </CardTitle>
        <CardDescription>
          Manage your rewards balance and auto-recharge settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Balance Display */}
        <div className="flex justify-between items-center bg-primary/5 p-4 rounded-md border">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Current Balance</p>
            <p className="text-3xl font-bold">${wallet.balance.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">
              {wallet.lastRechargeDate ? 
                `Last recharged on ${new Date(wallet.lastRechargeDate).toLocaleDateString()}` : 
                'No recent recharges'}
            </p>
          </div>
          <PiggyBank className="h-10 w-10 text-primary" />
        </div>

        {/* Wallet Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Wallet Settings</h3>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="auto-recharge"
              checked={autoRecharge}
              onCheckedChange={setAutoRecharge}
            />
            <Label htmlFor="auto-recharge">Auto-recharge when balance is low</Label>
          </div>

          {autoRecharge && (
            <div className="grid gap-4 pl-6 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min-balance">Minimum Balance ($)</Label>
                  <Input
                    id="min-balance"
                    type="number"
                    value={minimumBalance}
                    onChange={(e) => setMinimumBalance(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recharge-amount">Recharge Amount ($)</Label>
                  <Input
                    id="recharge-amount"
                    type="number"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="maintain-min"
                  checked={maintainMinimumBalance}
                  onCheckedChange={setMaintainMinimumBalance}
                />
                <Label htmlFor="maintain-min">Always maintain minimum balance</Label>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchWalletData}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button 
              onClick={saveWalletSettings} 
              disabled={isUpdating}
              size="sm"
            >
              {isUpdating ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminWallet;
